import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useMemo } from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { makeStyles } from "tss-react/mui";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";

import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";

import DataExplorerService from "../../../service/dataExplorer";
import KMKTable from "../../Common/Table/KMKTable";
import { sampleLimit } from "./statisticsData";

const useStyles = makeStyles()((theme) => ({
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
  },
}));

const dataExplorerService = new DataExplorerService();

const SampleData = ({ Workspace_id, tableName, schemaName }) => {
  const { classes } = useStyles();
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleData, setSampleData] = useState([]);

  const getSampleData = useCallback(async () => {
    setSampleLoading(true);
    const response = await dataExplorerService.getSampleData(Workspace_id, {
      schema: schemaName,
      table: tableName,
      sampleLimit,
    });

    if (response?.success) setSampleData(response.data || []);
    setSampleLoading(false);
  }, [Workspace_id, schemaName, tableName]);

  const appsColumns = useMemo(() => {
    if (sampleLoading) return [];
    return Object.keys(sampleData?.[0] || [])?.map((column) => ({
      id: column,
      name: column,
      style: { maxWidth: 290, wordBreak: "break-word", fontSize: "12px" },
      width: 150,
      align: "left",
      sortable: false,
      showColumnBorder: true,
      renderHeadCell: (name) => name,
      renderValueCell: (val, record, index) => record?.[column]?.toString() || "",
    }));
  }, [sampleData, sampleLoading]);

  useEffect(() => {
    getSampleData();

    return () => dataExplorerService.cancelRequest();
  }, [getSampleData, tableName]);

  return (
    <Grid sx={{ width: "100%", display: "flex", alignItems: "center" }}>
      <Box xs={12} sx={{ px: "10px", width: "100%", background: "#ffffff", borderRadius: "10px" }} elevation={2}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ width: "100%", textAlign: "center" }}>
            <span style={{ fontWeight: "bold", marginLeft: "40px", fontSize: "14px" }}>Preview Data</span>
          </Typography>

          <Tooltip title="Refresh Preview Data" placement="bottom" arrow>
            <IconButton onClick={() => getSampleData()} disabled={sampleLoading || sampleData?.length < sampleLimit}>
              <RefreshIcon sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
          <KMKTable
            loading={sampleLoading}
            columns={appsColumns}
            containerHeight={"500px"}
            sortingProperties={{}}
            paginationProperties={{}}
            noDataText="No data available"
            data={sampleData}
            boldHeader
            tableProps={{ size: "medium", stickyHeader: true }}
            dataIdKey="Sample_Data"
            unifiedView={true}
          />
        </Box>
      </Box>
    </Grid>
  );
};

SampleData.propTypes = {
  Workspace_id: PropTypes.string,
  schemaName: PropTypes.string,
  tableName: PropTypes.string,
};

export default React.memo(SampleData);
